/** @file The bundle puppet: entries resolved to rects, used tables inlined, effective profile, sheet files. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { effectiveProfile } from '@zilo/pose3d-format/profile/effectiveProfile';
import { BUILTIN_PROFILES } from '@zilo/pose3d-format/tables/builtinProfiles';
import { SECTOR_TABLES } from '@zilo/pose3d-format/tables/sectorTables';
import type { SectorTable } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { resolveEntries } from '@zilo/pose3d-export/resolve/resolveEntries';
import { tableOf } from '@zilo/pose3d-export/resolve/tableOf';
import type { BundleSheetRef, ExportDrawable, ExportPuppet, ResolvedEntry } from '@zilo/pose3d-export/types/bundle';
import type { ExportDeps } from '@zilo/pose3d-export/types/exportDeps';
import type { PackedBlocks } from '@zilo/pose3d-export/types/packBlock';

const jsonCopy = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const sheetFile = (name: string): string => `sheets/${name}.png`;

/** Tables the resolved entries use; a `yawFlip` entry also needs its table's `_FLIP` twin when one exists (18.4). */
function usedTables(puppet: Puppet, entries: ResolvedEntry[], builtins: Readonly<Record<string, SectorTable>>): Record<string, SectorTable> {
  const tables: Record<string, SectorTable> = {};
  for (const e of entries) {
    tables[e.yaw] = tableOf(e.yaw, puppet, builtins, '/puppet/sectorTables');
    const twin = `${e.yaw}_FLIP`;
    if (e.yawFlip === true && (Object.hasOwn(puppet.sectorTables ?? {}, twin) || Object.hasOwn(builtins, twin))) {
      tables[twin] = tableOf(twin, puppet, builtins, '/puppet/sectorTables');
    }
  }
  return jsonCopy(tables);
}

/** Names of the sheets the resolved drawables read: non-empty entries' sheets and effect strips. */
function sheetNames(drawables: ExportDrawable[]): Set<string> {
  const names = new Set<string>();
  for (const d of drawables) {
    if (d.kind === 'effect') names.add(d.frames.sheet);
    if (d.kind !== 'impostor') continue;
    for (const e of d.facets) if (e.rects.some((r) => r !== null)) names.add(e.sheet);
  }
  return names;
}

/** Authored sheets a non-packed entry or an effect strip reads, then the packed sheets. */
function usedSheets(puppet: Puppet, drawables: ExportDrawable[], packed: PackedBlocks): Record<string, BundleSheetRef> {
  const sheets: Record<string, BundleSheetRef> = {};
  for (const name of sheetNames(drawables)) {
    const sheet = Object.hasOwn(packed.sheets, name) ? undefined : puppet.sheets[name];
    if (sheet !== undefined) sheets[name] = { file: sheetFile(name), size: [...sheet.size] };
  }
  for (const [name, size] of Object.entries(packed.sheets)) {
    if (Object.hasOwn(puppet.sheets, name)) {
      throwError(ERROR_CODES.VALIDATION, { sheet: name }, { path: `/puppet/sheets/${name}`, hint: 'packed-<group> names belong to export; rename the sheet' });
    }
    sheets[name] = { file: sheetFile(name), size: [...size] };
  }
  return sheets;
}

/**
 * The bundle puppet of `puppet` given the packing `packed`: impostor entries resolved (resolveEntries), `tables` =
 * the used ids from `puppet.sectorTables` then `deps.sectorTables ?? SECTOR_TABLES`, `profileData` =
 * `effectiveProfile`, `sheets` = the used authored sheets plus the packed ones, `attachments` copied as metadata.
 */
export function resolvePuppet(puppet: Puppet, deps: Pick<ExportDeps, 'sectorTables' | 'profiles'>, packed: PackedBlocks): { puppet: ExportPuppet; warnings: Warning[] } {
  const source = jsonCopy(puppet);
  const builtins = deps.sectorTables ?? SECTOR_TABLES;
  const warnings: Warning[] = [];
  const entries: ResolvedEntry[] = [];
  const drawables: ExportDrawable[] = source.drawables.map((d, i) => {
    if (d.kind !== 'impostor') return d;
    const r = resolveEntries(source, d, i, builtins, packed.origins);
    warnings.push(...r.warnings);
    entries.push(...r.entries);
    return { ...d, facets: r.entries };
  });
  const out: ExportPuppet & Partial<Pick<Puppet, 'profileOverrides' | 'sectorTables'>> = {
    ...source, format: 'pose3d/puppet-bundle@1', drawables, sheets: usedSheets(source, drawables, packed),
    tables: usedTables(source, entries, builtins), profileData: effectiveProfile(source, deps.profiles ?? BUILTIN_PROFILES),
    attachments: source.attachments ?? [],
  };
  delete out.profileOverrides;
  delete out.sectorTables;
  return { puppet: out, warnings };
}
